"use client";

import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { useRoleCheck } from "@/lib/hooks/auth/useRoleCheck";

/**
 * One allocation for the not-yet-loaded / empty case. `data || []` built a
 * FRESH array on every render until the fetch resolved, so every downstream
 * memo keyed on `tags` (the config enhancers, and the table configs they
 * feed) was a no-op for the whole loading window (MEMO_STABLE_PROPS).
 */
const EMPTY_TAGS: string[] = [];

/**
 * Hook to fetch unique tags from contacts
 * Returns sorted list of all unique tags used across contacts
 * Uses server-side API with Aurora cache (30-minute TTL)
 */
export function useContactTags() {
  const { isEmployee } = useRoleCheck();
  const { data, isLoading, error } = useQuery({
    queryKey: ["tags", "contacts"],
    queryFn: async () => {
      const response = await fetch("/api/tags/contacts");
      if (!response.ok) throw new Error("Failed to fetch contact tags");
      const json = await response.json();
      return json.tags || [];
    },
    staleTime: 30 * 60 * 1000, // 30 minutes
    gcTime: 60 * 60 * 1000, // 60 minutes garbage collection
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    // CLIENT_USER_ACCESS_SCOPE: these endpoints aggregate tags across EVERY
    // company/contact/deal/ticket/task in the CRM. They are employee-only
    // server-side; without this gate a client-portal page load fired them all
    // and got a 403 per render.
    enabled: isEmployee,
  });

  return useMemo(
    () => ({ tags: data || EMPTY_TAGS, loading: isLoading, error }),
    [data, isLoading, error]
  );
}

/**
 * Hook to fetch unique tags from tickets
 * Returns sorted list of all unique tags used across tickets
 * Uses server-side API with Aurora cache (30-minute TTL)
 */
export function useTicketTags() {
  const { isEmployee } = useRoleCheck();
  const { data, isLoading, error } = useQuery({
    queryKey: ["tags", "tickets"],
    queryFn: async () => {
      const response = await fetch("/api/tags/tickets");
      if (!response.ok) throw new Error("Failed to fetch ticket tags");
      const json = await response.json();
      return json.tags || [];
    },
    staleTime: 30 * 60 * 1000, // 30 minutes
    gcTime: 60 * 60 * 1000, // 60 minutes garbage collection
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    // CLIENT_USER_ACCESS_SCOPE: these endpoints aggregate tags across EVERY
    // company/contact/deal/ticket/task in the CRM. They are employee-only
    // server-side; without this gate a client-portal page load fired them all
    // and got a 403 per render.
    enabled: isEmployee,
  });

  return useMemo(
    () => ({ tags: data || EMPTY_TAGS, loading: isLoading, error }),
    [data, isLoading, error]
  );
}

/**
 * Hook to fetch unique tags from contact tasks
 * Returns sorted list of all unique tags used across tasks
 * Uses server-side API with Aurora cache (30-minute TTL)
 */
export function useTaskTags() {
  const { isEmployee } = useRoleCheck();
  const { data, isLoading, error } = useQuery({
    queryKey: ["tags", "tasks"],
    queryFn: async () => {
      const response = await fetch("/api/tags/tasks");
      if (!response.ok) throw new Error("Failed to fetch task tags");
      const json = await response.json();
      return json.tags || [];
    },
    staleTime: 30 * 60 * 1000, // 30 minutes
    gcTime: 60 * 60 * 1000, // 60 minutes garbage collection
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    // CLIENT_USER_ACCESS_SCOPE: these endpoints aggregate tags across EVERY
    // company/contact/deal/ticket/task in the CRM. They are employee-only
    // server-side; without this gate a client-portal page load fired them all
    // and got a 403 per render.
    enabled: isEmployee,
  });

  return useMemo(
    () => ({ tags: data || EMPTY_TAGS, loading: isLoading, error }),
    [data, isLoading, error]
  );
}

/**
 * Hook to fetch unique tags from email templates
 * Returns sorted list of all unique tags used across email templates
 * Uses server-side API with Aurora cache (30-minute TTL)
 */
export function useEmailTemplateTags() {
  const { isEmployee } = useRoleCheck();
  const { data, isLoading, error } = useQuery({
    queryKey: ["tags", "email-templates"],
    queryFn: async () => {
      const response = await fetch("/api/tags/email-templates");
      if (!response.ok) throw new Error("Failed to fetch email template tags");
      const json = await response.json();
      return json.tags || [];
    },
    staleTime: 30 * 60 * 1000, // 30 minutes
    gcTime: 60 * 60 * 1000, // 60 minutes garbage collection
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    // CLIENT_USER_ACCESS_SCOPE: these endpoints aggregate tags across EVERY
    // company/contact/deal/ticket/task in the CRM. They are employee-only
    // server-side; without this gate a client-portal page load fired them all
    // and got a 403 per render.
    enabled: isEmployee,
  });

  return useMemo(
    () => ({ tags: data || EMPTY_TAGS, loading: isLoading, error }),
    [data, isLoading, error]
  );
}

/**
 * Hook to fetch unique tags from MSP documents
 * Returns sorted list of all unique tags used across MSP documents
 * Note: Still uses GraphQL as this wasn't in the migration scope
 */
const MSP_DOCUMENT_TAGS_RESULT = {
  tags: EMPTY_TAGS,
  loading: false,
  error: null as Error | null,
};

export function useMspDocumentTags() {
  // TODO: Migrate to server-side API if needed
  // For now, keeping GraphQL implementation
  return MSP_DOCUMENT_TAGS_RESULT;
}

/**
 * Hook to fetch unique tags from companies
 * Returns sorted list of all unique tags used across companies
 * Uses server-side API with Aurora cache (30-minute TTL)
 */
export function useCompanyTags() {
  const { isEmployee } = useRoleCheck();
  const { data, isLoading, error } = useQuery({
    queryKey: ["tags", "companies"],
    queryFn: async () => {
      const response = await fetch("/api/tags/companies");
      if (!response.ok) throw new Error("Failed to fetch company tags");
      const json = await response.json();
      return json.tags || [];
    },
    staleTime: 30 * 60 * 1000, // 30 minutes
    gcTime: 60 * 60 * 1000, // 60 minutes garbage collection
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    // CLIENT_USER_ACCESS_SCOPE: these endpoints aggregate tags across EVERY
    // company/contact/deal/ticket/task in the CRM. They are employee-only
    // server-side; without this gate a client-portal page load fired them all
    // and got a 403 per render.
    enabled: isEmployee,
  });

  return useMemo(
    () => ({ tags: data || EMPTY_TAGS, loading: isLoading, error }),
    [data, isLoading, error]
  );
}

/**
 * Hook to fetch unique tags from deals
 * Returns sorted list of all unique tags used across deals
 * Uses server-side API with Aurora cache (30-minute TTL)
 */
export function useDealTags() {
  const { isEmployee } = useRoleCheck();
  const { data, isLoading, error } = useQuery({
    queryKey: ["tags", "deals"],
    queryFn: async () => {
      const response = await fetch("/api/tags/deals");
      if (!response.ok) throw new Error("Failed to fetch deal tags");
      const json = await response.json();
      return json.tags || [];
    },
    staleTime: 30 * 60 * 1000, // 30 minutes
    gcTime: 60 * 60 * 1000, // 60 minutes garbage collection
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    // CLIENT_USER_ACCESS_SCOPE: these endpoints aggregate tags across EVERY
    // company/contact/deal/ticket/task in the CRM. They are employee-only
    // server-side; without this gate a client-portal page load fired them all
    // and got a 403 per render.
    enabled: isEmployee,
  });

  return useMemo(
    () => ({ tags: data || EMPTY_TAGS, loading: isLoading, error }),
    [data, isLoading, error]
  );
}

/**
 * Hook to fetch unique technologies from companies
 * Returns sorted list of all unique technologies used across companies
 * Uses server-side API with Aurora cache (30-minute TTL)
 */
export function useCompanyTechnologies() {
  const { isEmployee } = useRoleCheck();
  const { data, isLoading, error } = useQuery({
    queryKey: ["tags", "company-technologies"],
    queryFn: async () => {
      const response = await fetch("/api/tags/company-technologies");
      if (!response.ok) throw new Error("Failed to fetch company technologies");
      const json = await response.json();
      return json.technologies || [];
    },
    staleTime: 30 * 60 * 1000, // 30 minutes
    gcTime: 60 * 60 * 1000, // 60 minutes garbage collection
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    // CLIENT_USER_ACCESS_SCOPE: these endpoints aggregate tags across EVERY
    // company/contact/deal/ticket/task in the CRM. They are employee-only
    // server-side; without this gate a client-portal page load fired them all
    // and got a 403 per render.
    enabled: isEmployee,
  });

  return useMemo(
    () => ({ technologies: data || EMPTY_TAGS, loading: isLoading, error }),
    [data, isLoading, error]
  );
}
